import { useEffect, useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate } from 'react-router-dom'
import { ordersApi, type OrderRow } from '../lib/api'
import { db, ordersOnDay, type Order } from '../lib/db'
import { clock, money, qtyToText } from '../lib/money'
import { isOwner, useApp } from '../lib/store'
import { AppBar } from '../components/AppBar'
import { OrderDetailSheet } from '../components/OrderDetailSheet'
import { OrdersBoard } from '../components/OrdersBoard'

const toDayInput = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

export function HistoryScreen() {
  const nav = useNavigate()
  const say = useApp((s) => s.say)
  const owner = isOwner(useApp((s) => s.session?.role))
  const skin = useApp((s) => s.skin)

  const [day, setDay] = useState(() => toDayInput(new Date()))
  const [query, setQuery] = useState('')
  const [openId, setOpenId] = useState<string | null>(null)
  const [remote, setRemote] = useState<OrderRow[] | null>(null)
  const [loading, setLoading] = useState(false)

  /*
   * Hai nguồn, ghép lại:
   *   - Máy này: hiện ra ngay, tra được cả khi mất mạng, và có cả đơn chưa kịp đẩy lên.
   *   - Máy chủ: bổ sung đơn do máy khác trong quán bán.
   *
   * Máy này đứng trước vì nó mới hơn — đơn vừa huỷ xong mà chưa đồng bộ thì bản trên
   * máy mới là bản đúng.
   */
  const local = useLiveQuery(() => ordersOnDay(new Date(day)), [day], [] as Order[])

  /*
   * Đơn đang bán lấy riêng.
   *
   * Danh sách theo ngày dò theo giờ thu tiền, mà đơn chưa thu tiền thì chưa có giờ ấy —
   * nên không cách nào lọt vào. Bộ Modern có ô đếm "Đang bán" nên phải hỏi thêm câu này;
   * và chỉ hỏi khi đang xem hôm nay, vì bàn đang có khách là chuyện của hôm nay.
   */
  const openLocal = useLiveQuery(
    () => db.orders.where('status').equals('Open').toArray(),
    [],
    [] as Order[],
  )

  useEffect(() => {
    let alive = true
    setLoading(true)
    setRemote(null)

    ordersApi
      .list(day)
      .then((rows) => alive && setRemote(rows))
      .catch(() => alive && setRemote(null))
      .finally(() => alive && setLoading(false))

    return () => {
      alive = false
    }
  }, [day])

  const toRow = (o: Order): OrderRow => ({
    id: o.id,
    code: o.code,
    tableName: o.tableName,
    status: o.status,
    paidAt: o.paidAt,
    total: o.total,
    paymentMethod: o.paymentMethod,
    itemCount: o.items.length,
    voidedAt: o.voidedAt,
    deviceId: o.deviceId,
  })

  const rows = useMemo(() => {
    const byId = new Map<string, OrderRow>()

    for (const r of remote ?? []) byId.set(r.id, r)

    for (const o of local) byId.set(o.id, toRow(o))

    const q = query.trim().toLowerCase()
    const digits = q.replace(/\D/g, '')

    return [...byId.values()]
      .filter((r) => {
        if (!q) return true
        return (
          r.code.toLowerCase().includes(q) ||
          (r.tableName ?? '').toLowerCase().includes(q) ||
          (digits.length >= 3 && String(r.total).includes(digits))
        )
      })
      .sort((a, b) => (b.paidAt ?? '').localeCompare(a.paidAt ?? ''))
  }, [remote, local, query])

  // Bộ Classic vẫn chỉ liệt kê hoá đơn đã chốt, đúng như trước.
  const settled = useMemo(() => rows.filter((r) => r.status !== 'Open'), [rows])

  const good = settled.filter((r) => !r.voidedAt)
  const revenue = good.reduce((s, r) => s + r.total, 0)
  const voided = settled.length - good.length

  async function afterVoid() {
    try {
      setRemote(await ordersApi.list(day))
    } catch {
      /* mất mạng thì bản trên máy đã đúng rồi */
    }
    say('Đã huỷ hoá đơn')
  }

  if (skin === 'modern') {
    const isToday = day === toDayInput(new Date())
    const q = query.trim().toLowerCase()

    // Đơn đang bán chỉ ghép vào khi đang xem hôm nay, và vẫn phải lọt qua ô tìm kiếm.
    const openRows = (isToday ? openLocal : [])
      .map(toRow)
      .filter((r) => !q || r.code.toLowerCase().includes(q) || (r.tableName ?? '').toLowerCase().includes(q))

    return (
      <div className="screen">
        <OrdersBoard
          rows={[...openRows, ...settled]}
          day={day}
          onDay={setDay}
          canPickDay={owner}
          query={query}
          onQuery={setQuery}
          loading={loading}
          offline={remote === null && !loading}
          onVoided={afterVoid}
        />
      </div>
    )
  }

  return (
    <div className="screen">
      <AppBar
        title="Hoá đơn"
        sub={
          settled.length === 0
            ? loading
              ? 'Đang tải…'
              : 'Ngày này chưa bán được hoá đơn nào'
            : `${good.length} hoá đơn · ${money(revenue)}đ${voided > 0 ? ` · ${voided} đã huỷ` : ''}`
        }
        onBack={() => nav('/')}
      />

      <div className="body">
        <div className="field-row">
          {owner ? (
            <label className="field">
              <span>Ngày</span>
              <input
                type="date"
                value={day}
                onChange={(e) => setDay(e.target.value)}
                max={toDayInput(new Date())}
              />
            </label>
          ) : (
            <div className="field">
              <span>Ngày</span>
              <div className="pseudo-input">Hôm nay</div>
            </div>
          )}
          <label className="field">
            <span>Tìm</span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Số hoá đơn, bàn, số tiền"
              inputMode="search"
            />
          </label>
        </div>

        {remote === null && !loading && (
          <p className="hint">
            Chưa lấy được dữ liệu từ máy chủ — đang xem hoá đơn lưu trên máy này. Hoá đơn do máy
            khác trong quán bán sẽ hiện khi có mạng lại.
          </p>
        )}

        {settled.length === 0 ? (
          <div className="empty">
            <div className="empty-icon" aria-hidden="true">
              🧾
            </div>
            <p>{query ? `Không có hoá đơn nào khớp "${query}".` : 'Ngày này chưa bán hoá đơn nào.'}</p>
          </div>
        ) : (
          <div className="list">
            {settled.map((r) => (
              <button
                key={r.id}
                type="button"
                className={`row-btn bill-row${r.voidedAt ? ' is-void' : ''}`}
                onClick={() => setOpenId(r.id)}
              >
                <span className="row-text">
                  <b>
                    {r.paidAt ? clock(r.paidAt) : '—'}
                    {r.tableName ? ` · ${r.tableName}` : ''}
                  </b>
                  <small>
                    {r.code} · {qtyToText(r.itemCount * 1000)} món
                    {r.paymentMethod === 'Transfer' ? ' · chuyển khoản' : ''}
                  </small>
                </span>
                {r.voidedAt && <span className="diff-tag short">Đã huỷ</span>}
                <span className={`row-price num${r.voidedAt ? ' struck' : ''}`}>{money(r.total)}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {openId && (
        <OrderDetailSheet
          orderId={openId}
          onClose={() => setOpenId(null)}
          onVoided={async () => {
            setOpenId(null)
            // Kéo lại từ máy chủ để danh sách khớp với thứ vừa đổi.
            await afterVoid()
          }}
        />
      )}
    </div>
  )
}

/** Dùng ở màn hình khác khi cần mở nhanh hoá đơn cuối cùng. */
export const lastOrder = () => db.orders.orderBy('paidAt').reverse().first()
